// @ts-nocheck
import React, { useState, useEffect, useMemo, useRef } from "react";
import { ASSET_ICON_OPTIONS } from "../AssetMapView/utils/iconUtils";
import { IconPreviewCanvas } from "../AssetMapView/utils/IconPreviewCanvas";
import { COMPOSED_FORECAST_ICON_OPTIONS } from "../utils/composedForecastIconOptions";
import { ReactComponent as HomeSmile } from "../../assets/icons/home-smile.svg";
import { ReactComponent as GlobeClick } from "../../assets/icons/globe-06.svg";
import { ReactComponent as UploadIcon } from "../../assets/icons/upload-02.svg";
import AssetImage from "../AssetImage";
import { useSelector, useDispatch } from "react-redux";
import { setModalOpen, closeModalWithResult, setModalState, modalViewsConst } from "../../store/modalSlice";
import { getUrl } from "@aws-amplify/storage";
import {
  postAsset,
  fetchAssets,
  editAsset,
  fetchAssetsByGroupId,
} from "../../store/assetsSlice";
import {
  fetchComposedForecasts,
  fetchComposedForecastAssetLinks,
  syncComposedForecastAssetLinks,
} from "../../store/composedForecastSlice";
import { getMyOrganizations } from "../../store/userSlice";
import MapViewerReact from "../MapViewerReact";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  setFlyLocation,
  setPointExploreVisible,
} from "../../store/mapStateSlice";
import SearchMapBox from "../GeocodingSearchBox";
import { RootState } from "../../store";
import ModalBase from "./ModalBase";
import * as toGeoJSON from '@tmcw/togeojson';
import * as turf from '@turf/turf';
import { ImageUploadTrigger } from "../imageUploader/ImageUploadFlow";
import { downloadGeometry } from "../../utils/geojsonUtils";
import { buildOverviewModel } from "../../utils/groupOverviewMap";
import { usePostHogCapture } from "../../hooks/usePostHogCapture";
import { POSTHOG_EVENTS } from "../../utils/posthogEvents";

import Select, { Field, SelectOption } from "../ReusableComponents/Select";
import Switch from "../ReusableComponents/Switch";
import { DISPLAYED_CHANNELS_NONE, isWeatherHidden } from "../utils/forecastSchemaUtils";
// The add/edit form's own width; the map sub-view swaps to 2xl and back to this
const FORM_MODAL_SIZE = "md";

const latMessage = "range: (-90) - 90";
const lngMessage = "range: (-180) - 180";

// Option lists for the shared Select; the icon is the same preview the map and asset dashboard draw
const ASSET_ICON_SELECT_OPTIONS: SelectOption[] = [
  { value: "", label: "Default (Circle)", icon: <span className="h-5 w-5 rounded-full border-2 border-eai-ink-soft bg-eai-ink-faint" /> },
  ...ASSET_ICON_OPTIONS.map((opt) => ({ value: opt.value, label: opt.label, icon: <IconPreviewCanvas iconKey={opt.value} size={20} /> })),
];

const FORECAST_ICON_SELECT_OPTIONS: SelectOption[] = [
  { value: "", label: "Default", icon: <span className="h-4 w-4 rounded-full border border-eai-border-hover bg-eai-surface-active" /> },
  ...COMPOSED_FORECAST_ICON_OPTIONS.map(({ value, label, Icon }) => ({ value, label, icon: <Icon className="h-4 w-4 stroke-eai-neutral" /> })),
];

const AssetSchema = z.object({
  name: z.string().trim().min(2).max(50),
  assetGroupId: z
    .string()
    .min(5, { message: "Asset must belong to Asset Group" }),
  description: z.string().trim().max(150),
  lat: z.coerce.number().max(90, latMessage).min(-90, latMessage),
  lng: z.coerce.number().max(180, lngMessage).min(-180, lngMessage),
  elevation: z.coerce.number().min(-180), //Cody, mins? can depressions from dikes or similar occur?
  hasGeojson: z.boolean().optional(),
  // organizationId will be set based on the selected assetGroup
});
type AssetType = z.infer<typeof AssetSchema>;

interface NewAssetModalProps {
  initialAssetState?: any;
  modalType?: 'newAsset' | 'editAsset' | 'callback';
  onClose?: (result?: any) => void;
}

// Stands in for the asset being edited or created when working out what the map should show, so unsaved geometry counts too
const DRAFT_ASSET_ID = "__draft__";

const NewAssetModal: React.FC<NewAssetModalProps> = ({ 
  initialAssetState, 
  modalType = 'newAsset',
  onClose
}) => {
  // For backward compatibility with components that still use editFlag const isEditing = editFlag || modalType === 'editAsset';
  const [monetaryValue, setMonetaryValue] = useState({
    value: initialAssetState?.value?.value ?? 0,
    currency: initialAssetState?.value?.currency ?? "",
  });
  const [mapViewOpen, setMapViewOpen] = useState(false);
  const { capture } = usePostHogCapture();
  // Store original GeoJSON data for cancel operation
  const [originalGeojsonData, setOriginalGeojsonData] = useState(null);
  // For file uploads
  const fileInputRef = useRef(null);
  // For map reference to access map methods like fitBounds
  const mapRef = useRef(null);
  // For image uploads
  const [imageFile, setImageFile] = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState(null);

  // For forecast channel selection
  const [displayedChannels, setDisplayedChannels] = useState(
    initialAssetState?.displayedChannels || []
  );

  // For custom map icon selection
  const [selectedIcon, setSelectedIcon] = useState(
    initialAssetState?.icon || ''
  );

  // Keep forecast type checkboxes in sync when editing and the asset loads/changes
  useEffect(() => {
    if (modalType === "editAsset" || modalType === "callback") {
      setDisplayedChannels(initialAssetState?.displayedChannels ?? []);
      setSelectedIcon(initialAssetState?.icon || '');
    }
  }, [initialAssetState?.displayedChannels, initialAssetState?.icon, modalType]);

  // "Hide weather forecasts" is stored as the NONE marker in displayedChannels
  const weatherForecastsHidden = isWeatherHidden(displayedChannels);

  // Composed forecasts attached to this asset (many-to-many via ComposedForecastAssets join. `selectedComposedForecasts` is what the user has currently picked in the dropdown (one entry per chip, with optional per-link icon); `existingComposedForecastLinks` is the snapshot from the backend at modal-open time so we can diff on save (create/update/delete).
  const [selectedComposedForecasts, setSelectedComposedForecasts] = useState<
    { composedForecastID: string; icon: string | null; isDefault?: boolean }[]
  >([]);
  const [existingComposedForecastLinks, setExistingComposedForecastLinks] = useState<
    { id: string; composedForecastID: string; icon: string | null; isDefault?: boolean }[]
  >([]);
  // Set when saving the composed-forecast links fails (the asset itself may have saved); the modal stays open to show it
  const [composedLinkError, setComposedLinkError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    watch,
    formState: { errors },
  } = useForm<AssetType>({
    defaultValues: {
      name: initialAssetState?.name || "",
      assetGroupId: initialAssetState?.assetGroupId || "",
      description: initialAssetState?.description || "",
      lat: initialAssetState?.lat || 0,
      lng: initialAssetState?.lng || 0,
      elevation: initialAssetState?.elevation || 0,
    },
    resolver: zodResolver(AssetSchema),
    mode: "onChange",
  });

  const [mapViewerLatLng, setMapViewerLatLng] = useState([null, null]);
  const dispatch = useDispatch();
  const assetGroups = useSelector((state: RootState) => state.assetGroups);
  const mapState = useSelector((state: RootState) => state.mapState);
  const user = useSelector((state: RootState) => state.user);
  const modalState = useSelector((state: RootState) => state.modalState);
  const assetsState = useSelector((state: RootState) => state.assets);
  const composedForecastsMap = useSelector(
    (state: RootState) => state.composedForecasts?.forecasts ?? {}
  );
  const composedForecastsList = React.useMemo(
    () => Object.values(composedForecastsMap),
    [composedForecastsMap]
  );

  // Something for the asset page to show once weather is hidden: an active Custom View picked below, or a V1 processed model
  const hasCustomViewOrModel =
    selectedComposedForecasts.some((sel) => composedForecastsMap[sel.composedForecastID]?.isActive !== false) ||
    (initialAssetState?.processedModels?.items?.length ?? 0) > 0;

  // Load composed forecasts (picker source) once when the modal mounts; in edit/callback mode, also load this asset's existing link rows so we can pre-tick the boxes and later diff against them on save.
  useEffect(() => {
    dispatch(fetchComposedForecasts() as any);
  }, [dispatch]);

  useEffect(() => {
    const isEdit = modalType === "editAsset" || modalType === "callback";
    if (!isEdit || !initialAssetState?.id) {
      setExistingComposedForecastLinks([]);
      setSelectedComposedForecasts([]);
      return;
    }
    (async () => {
      const result: any = await dispatch(
        fetchComposedForecastAssetLinks(initialAssetState.id) as any
      );
      // At most one default: the first marked one, the same rule as the dashboard (defaultComposedForecastKey)
      const firstDefault = ((result?.payload as any[]) || []).find((r) => r.isDefault)?.id;
      const rows = ((result?.payload as any[]) || []).map((r) => ({ ...r, isDefault: r.id === firstDefault }));
      setExistingComposedForecastLinks(
        rows.map((r) => ({
          id: r.id,
          composedForecastID: r.composedForecastID,
          icon: r.icon ?? null,
          isDefault: !!r.isDefault,
        }))
      );
      setSelectedComposedForecasts(
        rows.map((r) => ({
          composedForecastID: r.composedForecastID,
          icon: r.icon ?? null,
          isDefault: !!r.isDefault,
        }))
      );
    })();
  }, [dispatch, modalType, initialAssetState?.id]);

  // Check if user is an admin
  const isAdmin = user?.details?.groups?.includes("Admin");
  
  // Watch for changes to assetGroupId
  const selectedAssetGroupId = watch("assetGroupId");

  // The form itself is a ModalBase modal, so __modalSize must never be cleared — dropping it sends ModalGlobal back to the legacy frame. Clearing restores the form's own size instead.
  const setModalSize = (size: "wideMap" | "lg" | "2xl" | typeof FORM_MODAL_SIZE = FORM_MODAL_SIZE) => {
    // Use current modalState.properties as the source of truth so we can reliably reset flags we previously set, even if initialAssetState props are stale.
    const currentProps = modalState?.properties || {};
    dispatch(setModalState({
      modalOpen: true,
      modalView: modalState.modalView,
      properties: { ...currentProps, __modalSize: size }
    }));
  };

  // Function to fetch existing asset image
  const getAssetImage = async () => {
    if (initialAssetState?.imageUrl) {
      try {
        const { url } = await getUrl({
          path: initialAssetState.imageUrl,
          options: {
            validateObjectExistence: true,
            expiresIn: 3600
          }
        });
        setImagePreviewUrl(url);
      } catch (error) {
        console.error("Error fetching asset image:", error);
      }
    }
  };

  useEffect(() => {
    if (modalType === 'editAsset' || modalType === 'callback') {
      if (initialAssetState?.value) {
        setMonetaryValue(initialAssetState.value);
      }
      
      // Load existing asset image if available
      getAssetImage();
      
      // Check if the asset has GeoJSON data and set it
      if (initialAssetState?.geojson) {
        try {
          // Handle both string (stored in DB) and object formats
          const parsedGeoJSON = typeof initialAssetState.geojson === 'string' 
            ? JSON.parse(initialAssetState.geojson) 
            : initialAssetState.geojson;
            
          setGeojsonData(parsedGeoJSON);
          // Store the original GeoJSON data for restoring on cancel
          setOriginalGeojsonData(parsedGeoJSON);
          setValue("hasGeojson", true);
          
          // If we have GeoJSON data, determine the type
          if (parsedGeoJSON.features && parsedGeoJSON.features.length > 0) {
            const featureType = parsedGeoJSON.features[0].geometry.type;
            // Set the current draw mode based on the geometry type
            if (featureType === "Point") setCurrentDrawMode("point");
            else if (featureType === "MultiPoint") setCurrentDrawMode("multipoint");
            else if (featureType === "LineString") setCurrentDrawMode("line");
            else if (featureType === "Polygon") setCurrentDrawMode("polygon");
          }
        } catch (error) {
          console.error("Error parsing GeoJSON data:", error);
        }
      } 
      // If no GeoJSON but we have lat/lng, create a point feature
      else if (initialAssetState?.lat && initialAssetState?.lng) {
        const pointGeoJSON = {
          type: "FeatureCollection",
          features: [{
            type: "Feature",
            geometry: {
              type: "Point",
              coordinates: [initialAssetState.lng, initialAssetState.lat]
            },
            properties: {}
          }]
        };
        
        setGeojsonData(pointGeoJSON);
        setOriginalGeojsonData(pointGeoJSON);
        setValue("hasGeojson", true);
        setCurrentDrawMode("point");
      }
    }

    if (modalType === 'callback') {
      setValue("assetGroupId", "placeholder")
    }

    return () => {
      dispatch(setPointExploreVisible({ active: false, lnglat: null }));
    };
  }, []);

  // Watch for image upload modal results
  useEffect(() => {
    if (
      modalState.lastResult &&
      modalState.lastResult.action === 'updateImage' &&
      modalState.lastResult.uploadTarget === 'asset' &&
      // Only react to image updates for *this* asset
      modalState.lastResult.assetId === initialAssetState?.id
    ) {
      // Handle the result from the image upload modal
      if (modalState.lastResult.success && modalState.lastResult.imageUrl) {
        // For editing, store the image path to update the asset
        setImageFile({ path: modalState.lastResult.imageUrl });
        
        // Create preview URL for the uploaded image
        const loadImagePreview = async () => {
          try {
            const { url } = await getUrl({
              path: modalState.lastResult.imageUrl,
              options: {
                validateObjectExistence: true,
                expiresIn: 3600
              }
            });
            setImagePreviewUrl(url.toString());
          } catch (error) {
            console.error("Error loading image preview:", error);
          }
        };
        loadImagePreview();
      }
    }
  }, [modalState.lastResult]);

  // Handle opening the image cropping modal
  const handleOpenImageModal = () => {
    // Create a complete asset object with all required fields for the modal
    const assetForModal = {
      id: initialAssetState?.id,
      name: initialAssetState?.name || getValues("name") || 'Asset',
      imageUrl: initialAssetState?.imageUrl || null,
      organizationId: initialAssetState?.organizationId || null,
      assetGroupId: initialAssetState?.assetGroupId || getValues("assetGroupId"),
      lat: initialAssetState?.lat || getValues("lat") || 0,
      lng: initialAssetState?.lng || getValues("lng") || 0,
      elevation: initialAssetState?.elevation || getValues("elevation") || 0,
      description: initialAssetState?.description || getValues("description") || ""
    };
    
    console.log("Opening image modal with asset data:", assetForModal);
    
    dispatch(setModalState({
      modalOpen: true,
      modalView: modalViewsConst.assetImageUpload,
      properties: assetForModal
    }));
  };

  const submitAsset = async (data) => {
    
    let assetToSubmit = { ...data };
    
    // Truncate lat/lng/elevation to prevent excessive decimal places
    if (typeof assetToSubmit.lat === 'number' || typeof assetToSubmit.lat === 'string') {
      assetToSubmit.lat = parseFloat(Number(assetToSubmit.lat).toFixed(5));
    }
    
    if (typeof assetToSubmit.lng === 'number' || typeof assetToSubmit.lng === 'string') {
      assetToSubmit.lng = parseFloat(Number(assetToSubmit.lng).toFixed(5));
    }
    
    if (typeof assetToSubmit.elevation === 'number' || typeof assetToSubmit.elevation === 'string') {
      assetToSubmit.elevation = parseFloat(Number(assetToSubmit.elevation).toFixed(2));
    }

    // Set organizationId based on the selected asset group
    const selectedAssetGroupId = data.assetGroupId;
    if (selectedAssetGroupId && assetGroups.groups[selectedAssetGroupId]) {
      // If the asset group has an organizationId, use it
      if (assetGroups.groups[selectedAssetGroupId].organizationId) {
        const organizationId = assetGroups.groups[selectedAssetGroupId].organizationId;
        assetToSubmit.organizationId = organizationId;
        
        // If user is an admin and organization data is loaded, set owner to organization owner
        if (isAdmin && user?.organizations?.loaded && user?.organizations?.data?.[organizationId]?.owner) {
          assetToSubmit.owner = user.organizations.data[organizationId].owner;
        }
      } else {
        // If the asset group has no organizationId, remove it from the asset if it exists
        assetToSubmit.organizationId = null;
      }
    }

    if (monetaryValue?.currency?.length > 0 && monetaryValue.value > 0) {
      const { value, currency } = monetaryValue;
      assetToSubmit.value = { value, currency };
    } else {
      assetToSubmit.value = null;
    }

    // Include displayedChannels if any are selected
    if (displayedChannels && displayedChannels.length > 0) {
      assetToSubmit.displayedChannels = displayedChannels;
    } else {
      assetToSubmit.displayedChannels = null;
    }

    // Include custom map icon
    assetToSubmit.icon = selectedIcon || null;
    
    // Add geojson data if available and the form indicates hasGeojson
    if (getValues("hasGeojson") && geojsonData && geojsonData.features && geojsonData.features.length > 0) {
      // Store GeoJSON for ALL geometry types, including Point
      const geoJsonString = JSON.stringify(geojsonData);
      
      // Log the GeoJSON size
      const sizeInBytes = new Blob([geoJsonString]).size;
      console.log(`Submitting GeoJSON - Size: ${(sizeInBytes / 1024).toFixed(2)}KB (${(sizeInBytes / 1024 / 1024).toFixed(4)}MB)`);
      
      assetToSubmit.geojson = geoJsonString;
      
      // Remove hasGeojson from the asset as it's not needed in the database
      delete assetToSubmit.hasGeojson;
    }
    
    // Include the ID if we're editing
    if (modalType === 'editAsset' || modalType === 'callback') {
      assetToSubmit.id = initialAssetState.id;
    }

    try {
      // Handle the submission based on modalType
      switch (modalType) {
        case 'callback':
          // Just return the data to the caller without any API calls if (onClose) { onClose({ success: true, asset: assetToSubmit, action: 'onClose' }); } else {
            dispatch(closeModalWithResult({
              success: true,
              asset: assetToSubmit,
              action: 'closeModalWithAssetResult'
            }));
          // }
          break;
          
        case 'editAsset':
          // Handle image upload if a new image was selected
          if (imageFile?.path) {
            // Image was already uploaded via the cropping modal
            assetToSubmit.imageUrl = imageFile.path;
          }
          
          // Call API through Redux and update the store Craig, it updates it fails, then refetch, fix this
          const updateResult = await dispatch(editAsset({ id: initialAssetState.id, ...assetToSubmit }));
          // Reconcile composed-forecast join rows for this asset against the current picker selection. Fire-and-forget here would race the refetch below, so await it first.
          setComposedLinkError(null);
          const linkResult: any = await dispatch(
            syncComposedForecastAssetLinks({
              assetId: initialAssetState.id,
              selectedForecasts: selectedComposedForecasts,
              existingLinks: existingComposedForecastLinks,
              owner: user?.details?.id,
              // The asset's org as saved now (taken from its group), so org members can read the link
              organizationId: assetToSubmit?.organizationId || initialAssetState?.organizationId || null,
            }) as any
          );
          await dispatch(fetchAssetsByGroupId(initialAssetState.assetGroupId));
          if (linkResult?.error) {
            // Keep the modal open so the user sees which part didn't save
            setComposedLinkError(String(linkResult.payload || "Some composed forecast changes couldn't be saved."));
            return;
          }

          // Close the modal, pass result to onClose if provided
          if (onClose) {
            onClose({
              success: true,
              asset: assetToSubmit,
              action: 'update'
            });
          } else {
            dispatch(closeModalWithResult({
              success: true,
              asset: assetToSubmit,
              action: 'update'
            }));
          }
          break;
          
        case 'newAsset':
        default:
          // Create a new asset through Redux
          const result = await dispatch(postAsset(assetToSubmit));
          // await dispatch(fetchAssets());
          
          // Add the generated ID to the asset data if available
          if (result?.payload?.id) {
            assetToSubmit.id = result.payload.id;

            // Handle image updates when an uploaded path already exists (from upload modal)
            if (imageFile?.path) {
              const assetWithImage = { ...assetToSubmit, imageUrl: imageFile.path };
              await dispatch(editAsset({ id: result.payload.id, ...assetWithImage }));
              assetToSubmit.imageUrl = imageFile.path;
            }

            // Attach any composed forecasts the user ticked in the picker. existingComposedForecastLinks is empty on create, so the diff util will treat everything selected as a create.
            if (selectedComposedForecasts.length > 0) {
              await dispatch(
                syncComposedForecastAssetLinks({
                  assetId: result.payload.id,
                  selectedForecasts: selectedComposedForecasts,
                  existingLinks: [],
                  owner: user?.details?.id,
                  organizationId: assetToSubmit?.organizationId || null,
                }) as any
              );
            }
          }
          
          // Close the modal, pass result to onClose if provided
          if (onClose) {
            onClose({
              success: true,
              asset: assetToSubmit,
              action: 'create'
            });
          } else {
            dispatch(closeModalWithResult({
              success: true,
              asset: assetToSubmit,
              action: 'create'
            }));
          }
          break;
      }
      
      // For backward compatibility with editFlag if (editFlag && modalType === 'newAsset') { // Legacy edit mode await dispatch(editAsset({ id: initialAssetState.id, ...assetToSubmit })); await dispatch(fetchAssetsByGroupId(initialAssetState.assetGroupId));
        
      // if (onClose) { onClose({ success: true, asset: assetToSubmit, action: 'update' }); } else { dispatch(closeModalWithResult({ success: true, asset: assetToSubmit, action: 'update' })); } }
      
    } catch (error) {
      console.error('Error saving asset:', error);
      
      // Handle error
      if (onClose) {
        onClose({
          success: false,
          error: error
        });
      } else {
        dispatch(closeModalWithResult({
          success: false,
          error: error
        }));
      }
    }
  };

  const handleFindOnMap = () => {
    // Toronto coordinates as default fallback
    const torontoCoords = [-79.3832, 43.6532];
    
    // Save current GeoJSON state before opening map view
    setOriginalGeojsonData(geojsonData);
    
    // Simple synchronous coordinates selection in priority order
    let selectedCoords = null;
    
    // Priority 1: Use existing GeoJSON data if available
    if (getValues("hasGeojson") && geojsonData && geojsonData.features && geojsonData.features.length > 0) {
      const feature = geojsonData.features[0];
      
      if (feature.geometry.type === "Point") {
        // For Point geometry, use the coordinates directly
        selectedCoords = feature.geometry.coordinates;
      } else {
        // For non-point geometries, calculate centroid
        try {
          const turf = require('@turf/turf');
          const centroid = turf.centroid(feature);
          selectedCoords = centroid.geometry.coordinates;
        } catch (error) {
          console.error("Error calculating centroid:", error);
        }
      }
    }
    
    // Priority 2: Use form lat/lng values if no GeoJSON or calculation failed
    if (!selectedCoords) {
      const lat = getValues("lat");
      const lng = getValues("lng");
      
      if (lat && lng && (lat !== 0 || lng !== 0)) {
        selectedCoords = [lng, lat];
      } else {
        // Priority 3: Fall back to Toronto if nothing else works
        selectedCoords = torontoCoords;
        console.log("Using Toronto fallback coordinates:", selectedCoords);
      }
    }
    
    // Set the coordinates in state
    setMapViewerLatLng(selectedCoords);
    
    // Update map state. No fly-to here: the map opens on these coordinates already, and a fly-to (zoom 7) would override the fit to the group's assets.
    dispatch(setPointExploreVisible({ active: true, lnglat: selectedCoords }));
    
    // Open the map view Widen the modal panel for easier map use (map view only)
    setModalSize("2xl");
    setMapViewOpen(true);
  };

  const handleSelectLocation = (e) => {
    const coords = [
      parseFloat(e.lngLat.lng.toFixed(5)),
      parseFloat(e.lngLat.lat.toFixed(5)),
    ];
    setMapViewerLatLng(coords);
    dispatch(setPointExploreVisible({ active: true, lnglat: coords }));
  };

  const handleConfirmMapLatLng = () => {
    // Set lat/lng values for the form with 5 decimal places
    if (mapViewerLatLng[0] !== null && mapViewerLatLng[1] !== null) {
      setValue("lng", parseFloat(mapViewerLatLng[0].toFixed(5)));
      setValue("lat", parseFloat(mapViewerLatLng[1].toFixed(5)));
    }
    
    // Process the GeoJSON data if available
    if (geojsonData && geojsonData.features.length > 0) {
      const feature = geojsonData.features[0];
      
      // For Point geometry, store both lat/lng AND the GeoJSON
      if (feature.geometry.type === "Point") {
        // We've already set lat/lng above, but we need to store the GeoJSON too
        setValue("hasGeojson", true);
      } 
      // For non-Point geometries (LineString, Polygon, etc.), store GeoJSON and calculate centroid
      else {
        // Store the GeoJSON in the form state
        setValue("hasGeojson", true);
        
        try {
          // Use turf.js to calculate centroid
          const turf = require('@turf/turf');
          const centroid = turf.centroid(feature);
          const [lng, lat] = centroid.geometry.coordinates;
          
          // Ensure coordinates are limited to 5 decimal places
          setValue("lng", parseFloat(lng.toFixed(5)));
          setValue("lat", parseFloat(lat.toFixed(5)));
        } catch (error) {
          console.error("Error calculating centroid:", error);
        }
      }
    } else {
      setValue("hasGeojson", false);
    }
    
    setMapViewOpen(false);
    // Restore default modal width after leaving map view
    setModalSize();
  };
  
  const handleCancelMap = () => {
    // Restore original GeoJSON data (if any)
    setGeojsonData(originalGeojsonData);
    
    // Set hasGeojson based on whether we have original data
    if (originalGeojsonData) {
      setValue("hasGeojson", true);
    } else {
      setValue("hasGeojson", false);
    }
    
    setMapViewOpen(false);
    // Restore default modal width after leaving map view
    setModalSize();
  };

  // Safety: ensure normal (non-map) view never keeps a wide modal
  useEffect(() => {
    if (!mapViewOpen && modalState?.properties?.__modalSize !== FORM_MODAL_SIZE) {
      setModalSize();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapViewOpen]);

  // Ensure Mapbox resizes when the modal panel widens
  useEffect(() => {
    if (!mapViewOpen) return;
    const t = setTimeout(() => {
      try {
        mapRef.current?.getMap?.()?.resize?.();
      } catch (e) {
        // ignore resize failures
      }
    }, 250);
    return () => clearTimeout(t);
  }, [mapViewOpen]);

  const handleGeoCodeSelect = (lngLat) => {
    const lnglat = [
      parseFloat(lngLat[0].toFixed(5)),
      parseFloat(lngLat[1].toFixed(5)),
    ];
    setMapViewerLatLng(lnglat);
    dispatch(setPointExploreVisible({ active: true, lnglat }));
    dispatch(setFlyLocation(lnglat));
  };
  
  // Function to get the appropriate modal title based on mode One close path for the header X and the footer Cancel — they must not drift apart
  const handleCancel = () => {
    if (onClose) {
      onClose();
    } else {
      dispatch(setModalOpen(false));
    }
  };

  const getModalTitle = () => {
    // Default to using editFlag for backward compatibility if (editFlag && modalType === 'newAsset') { return `Edit Existing Asset: ${initialAssetState?.name || ''}`; }
    
    // Use modalType for determining title
    switch (modalType) {
      case 'editAsset':
        return `Edit Existing Asset: ${initialAssetState?.name || ''}`;
      case 'callback':
        return `Preview Asset: ${initialAssetState?.name || ''}`;
      case 'newAsset':
      default:
        return 'New Asset Details';
    }
  };

  const [geojsonData, setGeojsonData] = useState(null);
  const [currentDrawMode, setCurrentDrawMode] = useState("point");

  // What the map opens on: the group's other assets with their real geometry (boundaries and routes, not just a centroid) to draw against, and bounds that take in the whole group so every asset is in view. The bounds also cover whatever the form already holds (an upload, or coordinates), so that can't open off-screen. Memoized because the modal re-renders on every draw click and the collection can be large; geojsonData is read but deliberately not a dependency, as it changes with each click.
  const { contextGeojson, groupBounds } = useMemo(() => {
    const none = { contextGeojson: null, groupBounds: null };
    if (!mapViewOpen) return none;

    // The group picked in the form wins, since a new asset opened from Home has none until the dropdown is used
    const groupId = getValues("assetGroupId") || initialAssetState?.assetGroupId;
    const groupAssets = (groupId && assetsState?.dataGroups?.[groupId]) || [];

    const formLat = getValues("lat");
    const formLng = getValues("lng");
    const hasFormCoords = formLat && formLng && (formLat !== 0 || formLng !== 0);
    const draft = geojsonData?.features?.length
      ? [{ id: DRAFT_ASSET_ID, geojson: geojsonData }]
      : hasFormCoords
        ? [{ id: DRAFT_ASSET_ID, lat: formLat, lng: formLng }]
        : [];

    const model = buildOverviewModel([...groupAssets, ...draft]);
    const others = model.collection.features.filter(
      (f) => f.properties.assetId !== DRAFT_ASSET_ID && f.properties.assetId !== initialAssetState?.id
    );
    return {
      contextGeojson: others.length ? { type: "FeatureCollection", features: others } : null,
      groupBounds: model.bounds,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapViewOpen, assetsState?.dataGroups, initialAssetState?.assetGroupId, initialAssetState?.id]);

  // TODO: REFACTOR - Replace this entire function with the utility from geojsonUtils.ts This function duplicates logic that has been extracted to src/utils/geojsonUtils.ts Steps to refactor: 1. Import: import { fileToGeoJSON } from '../../utils/geojsonUtils'; 2. Replace the entire handleFileUpload function body with: try { const processedGeoJson = await fileToGeoJSON(file); processGeoJsonData(processedGeoJson); // Call the local processGeoJsonData which handles map updates } catch (error) { console.error('Error processing file:', error); alert(error.message); } 3. Remove the FileReader, DOMParser, JSZip logic - it's all in fileToGeoJSON()
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    // Save current GeoJSON state before processing file
    setOriginalGeojsonData(geojsonData);

    const reader = new FileReader();
    const fileExtension = file.name.split('.').pop().toLowerCase();
    
    if (fileExtension === 'kml') {
      // Handle KML files
      reader.onload = (e) => {
        try {
          // Parse the KML file
          const kmlContent = e.target.result;
          const parser = new DOMParser();
          const kmlDoc = parser.parseFromString(kmlContent, 'text/xml');
          
          // Convert to GeoJSON
          const geoJsonData = toGeoJSON.kml(kmlDoc);
          
          // Process and set the GeoJSON data
          processGeoJsonData(geoJsonData);
        } catch (error) {
          console.error('Error parsing KML file:', error);
          alert('Error parsing KML file. Please make sure it is a valid KML file.');
        }
      };
      reader.readAsText(file);
    } else if (fileExtension === 'kmz') {
      // Handle KMZ files (compressed KML)
      reader.onload = async (e) => {
        try {
          // We need to unzip the KMZ file first Dynamically import JSZip as it's a large library
          const JSZip = (await import('jszip')).default;
          
          const zip = new JSZip();
          const contents = await zip.loadAsync(e.target.result);
          
          // KMZ typically contains a doc.kml file
          let kmlFile = contents.file('doc.kml');
          
          // If doc.kml doesn't exist, try to find any .kml file
          if (!kmlFile) {
            // Find the first .kml file in the archive
            const kmlFiles = Object.keys(contents.files).filter(filename => 
              filename.toLowerCase().endsWith('.kml')
            );
            
            if (kmlFiles.length > 0) {
              kmlFile = contents.file(kmlFiles[0]);
            } else {
              throw new Error('No KML file found in the KMZ archive');
            }
          }
          
          // Extract and parse the KML content
          const kmlContent = await kmlFile.async('string');
          const parser = new DOMParser();
          const kmlDoc = parser.parseFromString(kmlContent, 'text/xml');
          
          // Convert to GeoJSON
          const geoJsonData = toGeoJSON.kml(kmlDoc);
          
          // Process and set the GeoJSON data
          processGeoJsonData(geoJsonData);
        } catch (error) {
          console.error('Error processing KMZ file:', error);
          alert('Error processing KMZ file. Please make sure it is a valid KMZ file.');
          
          // If jszip isn't installed yet, show a more specific error
          if (error.message && error.message.includes("Cannot read properties of undefined")) {
            alert('JSZip library is needed to process KMZ files. Please install it with: npm install jszip');
          }
        }
      };
      reader.readAsArrayBuffer(file);
    } else {
      alert('Please upload a .kml or .kmz file');
    }
    
    // Clear the file input for future uploads
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  const handleDownloadGeometry = (format) => {
    if (!geojsonData?.features?.length) return;
    try {
      downloadGeometry(geojsonData, format, getValues("name"));
      capture(POSTHOG_EVENTS.ASSET_GEOMETRY_DOWNLOADED, {
        format,
        geometry_type: geojsonData.features[0]?.geometry?.type,
        feature_count: geojsonData.features.length,
      });
    } catch (error) {
      console.error("Error downloading geometry:", error);
      alert("Error downloading the geometry file. Please try again.");
    }
  };

  // Check GeoJSON size in bytes TODO: REFACTOR - Remove this function and use the utility from geojsonUtils.ts Replace with: import { getGeoJSONSize } from '../../utils/geojsonUtils'; This function is duplicated in src/utils/geojsonUtils.ts
  const getGeoJSONSize = (geojson) => {
    const stringified = JSON.stringify(geojson);
    return new Blob([stringified]).size;
  };

  // TODO: REFACTOR - Remove this function and use the utility from geojsonUtils.ts Replace with: import { reduceCoordinatePrecision } from '../../utils/geojsonUtils'; This function is duplicated in src/utils/geojsonUtils.ts Reduce coordinate precision to a specific number of decimal places
  const reduceCoordinatePrecision = (geojson, precision = 5) => {
    // Create a deep copy to avoid mutating the original
    const copy = JSON.parse(JSON.stringify(geojson));
    
    // Function to process coordinates recursively, reducing precision
    const processCoordinates = (coords) => {
      if (!Array.isArray(coords)) return coords;
      
      // If this is a coordinate pair [lng, lat] or [lng, lat, elevation]
      if (coords.length >= 2 && typeof coords[0] === 'number' && typeof coords[1] === 'number') {
        return coords.map(coord => 
          typeof coord === 'number' ? parseFloat(coord.toFixed(precision)) : coord
        );
      }
      
      // Otherwise it's an array of arrays (nested structure)
      return coords.map(c => processCoordinates(c));
    };
    
    // Process each feature in the collection
    if (copy.features && copy.features.length > 0) {
      copy.features = copy.features.map(feature => {
        if (feature.geometry && feature.geometry.coordinates) {
          feature.geometry.coordinates = processCoordinates(feature.geometry.coordinates);
        }
        return feature;
      });
    }
    
    return copy;
  };

  // TODO: REFACTOR - Remove this function and use the utility from geojsonUtils.ts Replace with: import { simplifyGeoJSON } from '../../utils/geojsonUtils'; This function is duplicated in src/utils/geojsonUtils.ts Simplify GeoJSON using Douglas-Peucker algorithm
  const simplifyGeoJSON = (geojson, tolerance) => {
    // Create a deep copy to avoid mutating the original
    const copy = JSON.parse(JSON.stringify(geojson));
    
    // Process each feature in the collection
    if (copy.features && copy.features.length > 0) {
      copy.features = copy.features.map(feature => {
        // Skip point geometries as they can't be simplified
        if (feature.geometry.type === "Point" || feature.geometry.type === "MultiPoint") {
          return feature;
        }
        
        // Use turf.simplify with Douglas-Peucker algorithm
        try {
          const simplified = turf.simplify(feature, {
            tolerance: tolerance,
            highQuality: true // Use Douglas-Peucker algorithm
          });
          return simplified;
        } catch (error) {
          console.error("Error simplifying feature:", error);
          return feature; // Return original on error
        }
      });
    }
    
    return copy;
  };

  // TODO: REFACTOR - Simplify this function to use utilities from geojsonUtils.ts This function has TWO parts: Part 1: GeoJSON processing (lines ~806-874) - DUPLICATED in geojsonUtils.processGeoJsonData() Part 2: UI updates (lines ~875-920) - UNIQUE to NewAssetModal, keep this Steps to refactor: 1. Import: import { processGeoJsonData as processGeoJson } from '../../utils/geojsonUtils'; 2. Replace lines ~806-874 with: const processedGeoJson = processGeoJson(geoJsonData); 3. Keep the UI-specific logic (setGeojsonData, setCurrentDrawMode, setMapViewerLatLng, etc.) 4. Remove local getGeoJSONSize, reduceCoordinatePrecision, simplifyGeoJSON calls
  const processGeoJsonData = (geoJsonData) => {
    // Handle case where we might not have a FeatureCollection
    let processedGeoJson = geoJsonData;
    
    if (geoJsonData.type !== 'FeatureCollection') {
      // Convert single feature to FeatureCollection
      processedGeoJson = {
        type: 'FeatureCollection',
        features: [geoJsonData]
      };
    }
    
    // If no features, alert and return
    if (!processedGeoJson.features || processedGeoJson.features.length === 0) {
      alert('No valid features found in the uploaded file.');
      return;
    }
    
    // First, reduce coordinate precision to 5 decimal places This is approximately 1.1 meters of precision at the equator, which is more than enough for most applications
    const PRECISION = 5;
    processedGeoJson = reduceCoordinatePrecision(processedGeoJson, PRECISION);
    
    // Check file size - 0.4MB (400KB) limit
    const MAX_SIZE_BYTES = 0.4 * 1024 * 1024; // 0.4MB in bytes
    const originalSize = getGeoJSONSize(processedGeoJson);
    
    // Log original GeoJSON object (after precision reduction)
    console.log("Original GeoJSON (with reduced precision):", processedGeoJson);
    console.log(`GeoJSON size after precision reduction: ${(originalSize/1024).toFixed(2)}KB (${(originalSize/1024/1024).toFixed(4)}MB)`);
    
    // If GeoJSON still exceeds size limit after precision reduction, apply simplification
    if (originalSize > MAX_SIZE_BYTES) {
      // Start with a small tolerance and gradually increase until size is acceptable
      let tolerance = 0.0001; // Starting tolerance
      let simplifiedGeoJson = processedGeoJson;
      let currentSize = originalSize;
      let attempts = 0;
      const MAX_ATTEMPTS = 10; // Prevent infinite loops
      
      while (currentSize > MAX_SIZE_BYTES && attempts < MAX_ATTEMPTS) {
        simplifiedGeoJson = simplifyGeoJSON(processedGeoJson, tolerance);
        currentSize = getGeoJSONSize(simplifiedGeoJson);
        
        // Increase tolerance for next iteration if needed
        tolerance *= 2;
        attempts++;
      }
      
      // Use the simplified version if we were able to reduce the size
      if (currentSize < originalSize) {
        // Log simplified GeoJSON object
        console.log("Simplified GeoJSON:", simplifiedGeoJson);
        console.log(`Simplified GeoJSON size: ${(currentSize/1024).toFixed(2)}KB (${(currentSize/1024/1024).toFixed(4)}MB)`);
        
        processedGeoJson = simplifiedGeoJson;
        console.log(`GeoJSON simplified: ${(originalSize/1024).toFixed(2)}KB → ${(currentSize/1024).toFixed(2)}KB (tolerance: ${tolerance/2})`);
        
        // Log simplification results to console instead of showing alerts
        if (currentSize <= MAX_SIZE_BYTES) {
          console.log(`Success: The uploaded geometry was simplified to meet the 0.4MB size limit.`);
        } else {
          console.warn(`Warning: The uploaded geometry is still ${(currentSize/1024/1024).toFixed(2)}MB after simplification (exceeds 0.4MB limit).`);
        }
      } else {
        console.warn(`Warning: The uploaded geometry (${(originalSize/1024/1024).toFixed(2)}MB) exceeds the 0.4MB limit and could not be simplified.`);
      }
    }
    
    // Update the geojson data
    setGeojsonData(processedGeoJson);
    
    // Calculate centroid for the first feature
    const feature = processedGeoJson.features[0];
    try {
      let featureType = feature.geometry.type;
      
      // Set the current draw mode based on the geometry type
      if (featureType === "Point") {
        setCurrentDrawMode("point");
        setMapViewerLatLng(feature.geometry.coordinates);
      } else if (featureType === "MultiPoint") {
        setCurrentDrawMode("multipoint");
        // Calculate and set centroid for MultiPoint
        const centroidPoint = turf.centroid(feature);
        setMapViewerLatLng(centroidPoint.geometry.coordinates);
      } else if (featureType === "LineString") {
        setCurrentDrawMode("line");
        // Calculate and set centroid for LineString
        const centroidPoint = turf.centroid(feature);
        setMapViewerLatLng(centroidPoint.geometry.coordinates);
      } else if (featureType === "Polygon") {
        setCurrentDrawMode("polygon");
        // Calculate and set centroid for Polygon
        const centroidPoint = turf.centroid(feature);
        setMapViewerLatLng(centroidPoint.geometry.coordinates);
      } else {
        // For complex types like MultiPolygon, MultiLineString, GeometryCollection We'll need to adapt our UI to handle these properly
        alert(`Feature type ${featureType} detected. It will be converted to a simpler geometry.`);
        
        // For now, calculate centroid and treat it as a point
        const centroidPoint = turf.centroid(feature);
        setMapViewerLatLng(centroidPoint.geometry.coordinates);
        setCurrentDrawMode("point");
      }
      
      // Set hasGeojson = true for the form
      setValue("hasGeojson", true);
      
      // If we're in the map view, fly to the feature
      if (mapViewOpen) {
        try {
          // Calculate bounds for the feature
          const bounds = turf.bbox(feature);
          
          // Check if bounds are valid (have some area)
          const isValidBounds = 
            Math.abs(bounds[0] - bounds[2]) > 0.00001 || 
            Math.abs(bounds[1] - bounds[3]) > 0.00001;
          
          if (isValidBounds && mapRef.current) {
            // Use reduced padding for better visibility
            const isMobile = window.innerWidth < 768;
            const padding = isMobile ? 10 : 20;
            
            // Format bounds for mapbox: [[west, south], [east, north]]
            const mapboxBounds = [[bounds[0], bounds[1]], [bounds[2], bounds[3]]];
            
            // Fit bounds with reduced padding
            mapRef.current.fitBounds(mapboxBounds, { 
              padding: padding,
              duration: 1500
            });
          } else {
            // If bounds are too small or invalid, just fly to centroid
            dispatch(setFlyLocation(mapViewerLatLng));
          }
        } catch (error) {
          console.error("Error calculating bounds:", error);
          // Fallback to centroid if bounds calculation fails
          dispatch(setFlyLocation(mapViewerLatLng));
        }
      } else {
        // Open map view to show the imported feature Widen the modal panel like the draw-on-map path, so the map-view height overrides on ModalBase (keyed to 2xl's 1232px full-screen breakpoint) apply to this entry path too.
        setModalSize("2xl");
        setMapViewOpen(true);
      }
      
    } catch (error) {
      console.error("Error processing GeoJSON from KML/KMZ:", error);
      alert('Error processing the geometry from the uploaded file.');
    }
  };

  const handleGeojsonUpdate = (geojson, centroidCoords = null, isInitialCheck = false) => {
    // If this is an initial check request, return any existing GeoJSON data
    if (isInitialCheck === true) {
      // Return existing GeoJSON data if available
      return geojsonData;
    }
    
    // Prevent unnecessary state updates
    if (!geojson || JSON.stringify(geojsonData) === JSON.stringify(geojson)) {
      return;
    }
    
    setGeojsonData(geojson);
    
    // If there's at least one feature with coordinates
    if (geojson && geojson.features && geojson.features.length > 0) {
      const feature = geojson.features[0];
      
      // For Point type, use the coordinates directly for lat/lng
      if (feature.geometry && feature.geometry.type === "Point") {
        const [lng, lat] = feature.geometry.coordinates;
        // Only update if coordinates have changed
        if (mapViewerLatLng[0] !== lng || mapViewerLatLng[1] !== lat) {
          setMapViewerLatLng([lng, lat]);
        }
      } 
      // For non-point geometries, use the centroid coordinates if provided
      else if (centroidCoords) {
        const [lng, lat] = centroidCoords;
        // Only update if coordinates have changed
        if (mapViewerLatLng[0] !== lng || mapViewerLatLng[1] !== lat) {
          setMapViewerLatLng([lng, lat]);
        }
      }
    }
  };

  if (mapViewOpen) {
    // Calculate appropriate default coordinates for the map
    let defaultMapLat = mapViewerLatLng[1] || 0;
    let defaultMapLng = mapViewerLatLng[0] || 0;
    
    // If mapViewerLatLng is not set yet, use form values
    if (!mapViewerLatLng[0] && !mapViewerLatLng[1]) {
      const formLat = getValues("lat");
      const formLng = getValues("lng");
      if (formLat && formLng && (formLat !== 0 || formLng !== 0)) {
        defaultMapLat = formLat;
        defaultMapLng = formLng;
      }
    }
    
    const mapFooter = (
      <>
        <button className="eai-btn-outline" type="button" onClick={handleCancelMap}>
          Cancel
        </button>
        <button
          key="map-confirm"
          type="button"
          onClick={handleConfirmMapLatLng}
          disabled={!mapViewerLatLng[0]}
          className="eai-btn-cta"
        >
          Confirm
        </button>
      </>
    );

    return (
      <ModalBase
        title="Select on Map or Draw Features"
        icon={<GlobeClick className="h-6 w-6" />}
        onClose={() => setMapViewOpen(false)}
        footer={mapFooter}
        size="lg"
        fillHeight
        // Below 2xl's full-screen breakpoint (1200px + 32px margin — the map view always dispatches __modalSize "2xl"), fill the visible viewport so the footer pins to the bottom of the screen instead of being clipped by mobile browser chrome.
        className="max-[1232px]:h-[100dvh] max-[1232px]:max-h-[100dvh]"
      >
        <div className="flex flex-col h-full min-h-0">
          <p className="eai-description-sm mb-3 shrink-0">
            Use the search box and drawing tools below to define your asset.
          </p>
          <MapViewerReact
            ref={mapRef}
            defaultLat={defaultMapLat}
            defaultLng={defaultMapLng}
            OnClick={handleSelectLocation}
            onGeojsonUpdate={handleGeojsonUpdate}
            onDrawModeChange={(mode) => setCurrentDrawMode(mode)}
            contextGeojson={contextGeojson}
            initialBounds={groupBounds}
            fillHeight
          >
            <SearchMapBox onSelected={handleGeoCodeSelect} />
          </MapViewerReact>
        </div>
      </ModalBase>
    );
  }

  return (
    <ModalBase
      title={getModalTitle()}
      icon={<HomeSmile className="h-6 w-6" />}
      onClose={handleCancel}
      size="md"
      footer={
        <>
          <button className="eai-btn-outline" type="button" onClick={handleCancel}>
            Cancel
          </button>
          <button key="asset-submit" className="eai-btn-cta" type="submit" form="asset-form">
            {modalType === 'newAsset' ? 'Submit' : 'Update'}
          </button>
        </>
      }
    >
      <form onSubmit={handleSubmit(submitAsset)} id="asset-form" className="flex flex-col gap-5">
        <Field label="Name" htmlFor="asset-name" error={errors.name?.message}>
          <input
            id="asset-name"
            name="name"
            {...register("name")}
            data-testid="asset-name-input"
            placeholder="e.g. Alberta Operations Site"
            className="eai-input"
          />
        </Field>
        
        {/* Asset Image Upload Section - Only show for existing assets. This is where image uploading starts */}
        {modalType === 'editAsset' && initialAssetState?.id && (
          <ImageUploadTrigger
            label="Asset Image (Optional)"
            onClick={handleOpenImageModal}
            previewNode={
              <AssetImage
                asset={initialAssetState}
                assetName={initialAssetState?.name || 'Asset'}
                size={64}
                fallbackIcon={<HomeSmile className="h-8 w-8 text-eai-ink-faint" />}
                className="rounded-eai-card border border-eai-border"
              />
            }
          />
        )}
        
        <Field label="Location">
          <div className="flex flex-col">
            <div className="flex flex-col sm:flex-row gap-2 w-full">
              <button
                type="button"
                className="eai-btn-outline sm:flex-1"
                onClick={handleFindOnMap}
              >
                <GlobeClick className="h-5 w-5 [&_path]:stroke-current" /> Select on Map or Draw Features
              </button>
              
              <div className="relative flex-1">
                <input
                  type="file"
                  accept=".kml,.kmz"
                  onChange={handleFileUpload}
                  ref={fileInputRef}
                  className="hidden"
                />
                <button
                  type="button"
                  className="eai-btn-outline w-full"
                  onClick={() => fileInputRef.current.click()}
                >
                  <UploadIcon className="h-5 w-5 [&_path]:stroke-current" /> Upload KML/KMZ
                </button>
              </div>
            </div>
            
            {getValues("hasGeojson") && (
              <>
                <div className="mt-2 text-center font-outfit text-sm text-eai-cta-primary">
                  ✓ {
                    geojsonData && geojsonData.features.length > 0
                      ? geojsonData.features[0].geometry.type
                      : (currentDrawMode === "line" ? "LineString" : currentDrawMode === "rectangle" ? "Polygon" : currentDrawMode.charAt(0).toUpperCase() + currentDrawMode.slice(1))
                  } Geometry Set
                </div>
                {geojsonData?.features?.length > 0 && (
                  <div className="mt-1 text-center font-inter text-eai-body-xs text-eai-ink-subtle">
                    Download:{" "}
                    <button
                      type="button"
                      className="text-eai-cta-primary underline hover:no-underline"
                      onClick={() => handleDownloadGeometry("geojson")}
                    >
                      GeoJSON
                    </button>
                    {" · "}
                    <button
                      type="button"
                      className="text-eai-cta-primary underline hover:no-underline"
                      onClick={() => handleDownloadGeometry("kml")}
                    >
                      KML
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </Field>

        <div className="flex md:flex-row flex-col gap-2 justify-between">
          <Field label="Latitude" htmlFor="asset-lat" error={errors.lat?.message} className="flex-1">
            <input
              id="asset-lat"
              name="lat"
              type="number"
              step="any"
              inputMode="decimal"
              {...register("lat")}
              data-testid="asset-lat-input"
              className="eai-input"
            />
          </Field>
          <Field label="Longitude" htmlFor="asset-lng" error={errors.lng?.message} className="flex-1">
            <input
              id="asset-lng"
              type="number"
              name="lng"
              step="any"
              inputMode="decimal"
              {...register("lng")}
              data-testid="asset-lng-input"
              className="eai-input"
            />
          </Field>
          <Field label="Elevation (m)" htmlFor="asset-elevation" error={errors.elevation?.message} className="flex-1">
            <input
              id="asset-elevation"
              name="elevation"
              type="number"
              step="any"
              inputMode="decimal"
              {...register("elevation")}
              data-testid="asset-elevation-input"
              className="eai-input"
            />
          </Field>
        </div>
        {modalType !== 'callback' ? <>
        <Field label="Asset Group" htmlFor="asset-group" error={errors.assetGroupId?.message}>
          <Select
            id="asset-group"
            name="assetGroupId"
            {...register("assetGroupId")}
          >
            <option value="">Select an Asset Group</option>
            {Object.keys(assetGroups.groups).map((assetGroupId) => {
              const group = assetGroups.groups[assetGroupId];
              // Enable if: 1. User is admin, OR 2. Group has no organization, OR 3. User is the owner of the group, OR 4. We're editing an asset that already belongs to this group
              const isUserGroupOwner = group.owner === user?.permissionDetails?.id;
              const isDisabled = group?.organizationId && 
                                !isAdmin && 
                                !isUserGroupOwner && 
                                !(modalType === 'editAsset' && initialAssetState?.assetGroupId === assetGroupId);
              
              // Get the organization name if available
              const orgName = group?.organizationId && user?.organizations?.data?.[group.organizationId]?.name;
              
              return (
                <option 
                  key={assetGroupId} 
                  value={assetGroupId} 
                  disabled={isDisabled}
                >
                  {group.name}{orgName ? ` (${orgName})` : ""}
                </option>
              );
            })}
            {/* Add more options as needed */}
          </Select>
        </Field>

        {/* Display organization info if available from selected asset group */}
        {/* {selectedAssetGroupId && assetGroups.groups[selectedAssetGroupId]?.organizationId && ( <> <div className="text-sm text-eai-warning-text mb-5"> This asset will be associated with organization: <span className="font-semibold ml-1"> {user?.organizations?.data?.[assetGroups.groups[selectedAssetGroupId].organizationId]?.name || "Organization"} </span> </div> </> )} */}
        </>
        :<></>
        }

        <div className="border-t border-eai-border mb-5"></div>
        <div className="flex gap-2 justify-between">
          <Field label="Asset Value" htmlFor="asset-value" className="flex-1">
            <input
              id="asset-value"
              step={0.01}
              name="monValue"
              type="number"
              min="0"
              value={monetaryValue.value}
              onChange={(e) =>
                setMonetaryValue({ ...monetaryValue, value: e.target.value })
              }
              className="eai-input"
            />
          </Field>
          <Field label="Asset Value Units" htmlFor="asset-currency" className="flex-1">
              <Select
                id="asset-currency"
                name="currency"
                value={monetaryValue.currency}
                onChange={(e) =>
                  setMonetaryValue({
                    ...monetaryValue,
                    currency: e.target.value,
                  })
                }
              >
                <option value="">None</option>
                <option value="USD">USD</option>
                <option value="CAD">CAD</option>
                <option value="MW">MW</option>
              </Select>
          </Field>
        </div>

        {/* Map Icon Selection */}
        <Field label="Map Icon">
          <Select value={selectedIcon} onChange={(e) => setSelectedIcon(e.target.value)} options={ASSET_ICON_SELECT_OPTIONS} />
        </Field>

        <Field label="Asset Description" htmlFor="asset-description" error={errors.description?.message}>
          <textarea
            id="asset-description"
            name="description"
            {...register("description")}
            rows={3}
            placeholder="What is this asset, and what is it used for?"
            className="eai-input"
          />
        </Field>

        {/* Display Channel Selection */}
        <Field
          label="Displayed Forecast Types (Optional)"
          hint={
            weatherForecastsHidden
              ? "Weather forecasts are hidden. The asset only shows its Custom Views and models."
              : "Select which forecast types should be available for this asset. If none selected, all types will be shown."
          }
        >
          <div className="mb-3 flex items-center gap-2">
            <Switch
              checked={weatherForecastsHidden}
              onChange={(next) => setDisplayedChannels(next ? [DISPLAYED_CHANNELS_NONE] : [])}
              label="Hide weather forecasts"
            />
            <span className="font-inter text-sm text-eai-ink-soft">Hide weather forecasts</span>
          </div>
          {weatherForecastsHidden && !hasCustomViewOrModel && (
            <p className="eai-description-sm mb-3 text-eai-warning-text">
              No Custom View attached to this asset. Its page will be empty until you attach one below.
            </p>
          )}
          <div className={`grid grid-cols-2 gap-3 ${weatherForecastsHidden ? "opacity-50" : ""}`}>
            {[
              { key: 'Precipitation', label: 'Precipitation' },
              { key: 'Wind10', label: 'Wind (10m)' },
              { key: 'Wind80', label: 'Wind (80m)' },
              { key: 'Temperature', label: 'Temperature' },
              { key: 'Humidity', label: 'Humidity' },
              { key: 'Solar', label: 'Solar' },
              { key: 'SoilMoisture', label: 'Soil Moisture' },
              { key: 'SnowAcc', label: 'Snow Water' },
            ].map((channel) => (
              <label
                key={channel.key}
                className={`flex items-center space-x-2 ${weatherForecastsHidden ? "cursor-not-allowed" : "cursor-pointer"}`}
              >
                <input
                  type="checkbox"
                  disabled={weatherForecastsHidden}
                  checked={displayedChannels.includes(channel.key)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setDisplayedChannels([...displayedChannels, channel.key]);
                    } else {
                      setDisplayedChannels(
                        displayedChannels.filter((c) => c !== channel.key)
                      );
                    }
                  }}
                  className="h-4 w-4 rounded border-eai-border-hover accent-eai-cta-primary focus:ring-eai-cta-primary"
                />
                <span className="font-inter text-sm text-eai-ink-soft">{channel.label}</span>
              </label>
            ))}
          </div>
        </Field>

        {/* Composed Forecasts selection — many-to-many via ComposedForecastAssets join. Dropdown checklist for picking + chip list below for per-link icons. */}
        <Field
          label="Composed Forecasts (Optional)"
          hint="Attach one or more Composed Forecasts to this asset. They appear in the asset's forecast dropdown, and the dashboard opens on the Default one (or the first)."
        >
          {composedForecastsList.length === 0 ? (
            <p className="eai-description-sm mt-2">
              No composed forecasts available. Create one under Admin → Composed Forecasts.
            </p>
          ) : (
            <>
              <Select
                multiple
                placeholder="Select composed forecasts…"
                value={selectedComposedForecasts.map((s) => s.composedForecastID)}
                onChange={(e) => {
                  const ids = Array.from(e.target.selectedOptions, (o) => o.value).filter(Boolean);
                  // Keep each kept link's icon; a newly picked forecast starts on the default
                  setSelectedComposedForecasts((prev) =>
                    ids.map((id) => prev.find((s) => s.composedForecastID === id) ?? { composedForecastID: id, icon: null })
                  );
                }}
                options={composedForecastsList.map((cf: any) => ({
                  value: cf.id,
                  label: cf.isActive === false ? `${cf.name} (inactive)` : cf.name,
                }))}
              />

              {/* Selected forecasts — one chip each, with icon picker + remove. */}
              {selectedComposedForecasts.length > 0 && (
                <div className="mt-3 flex flex-col gap-2">
                  {selectedComposedForecasts.map((sel) => {
                    const cf: any = composedForecastsList.find(
                      (c: any) => c.id === sel.composedForecastID
                    );
                    return (
                      <div
                        key={sel.composedForecastID}
                        className="flex items-center gap-2 rounded-eai-card border border-eai-border bg-eai-surface-sunken px-3 py-2"
                      >
                        <span className="flex-1 truncate font-inter text-sm text-eai-ink-soft">
                          {cf?.name || sel.composedForecastID}
                        </span>
                        {/* An inactive forecast never opens on the dashboard, so it can't be the default */}
                        {selectedComposedForecasts.length > 1 && cf?.isActive !== false && (
                          <label className="flex shrink-0 cursor-pointer items-center gap-1.5 font-inter text-xs text-eai-ink-soft">
                            <input
                              type="radio"
                              name="default-composed-forecast"
                              checked={!!sel.isDefault}
                              onChange={() =>
                                setSelectedComposedForecasts((prev) =>
                                  prev.map((s) => ({
                                    ...s,
                                    isDefault: s.composedForecastID === sel.composedForecastID,
                                  }))
                                )
                              }
                            />
                            Default
                          </label>
                        )}
                        <div className="w-44">
                          <Select
                            size="sm"
                            aria-label="Forecast icon"
                            options={FORECAST_ICON_SELECT_OPTIONS}
                            value={sel.icon ?? ""}
                            onChange={({ target: { value: icon } }) =>
                              setSelectedComposedForecasts((prev) =>
                                prev.map((s) =>
                                  s.composedForecastID === sel.composedForecastID
                                    ? { ...s, icon: icon || null }
                                    : s
                                )
                              )
                            }
                          />
                        </div>
                        <button
                          type="button"
                          aria-label="Remove composed forecast"
                          onClick={() =>
                            setSelectedComposedForecasts((prev) =>
                              prev.filter(
                                (s) => s.composedForecastID !== sel.composedForecastID
                              )
                            )
                          }
                          className="px-1 text-lg leading-none text-eai-ink-faint transition-colors hover:text-eai-ink"
                        >
                          ×
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
          {composedLinkError && (
            <p className="mt-2 font-inter text-xs text-eai-danger-text" role="alert">
              {composedLinkError}
            </p>
          )}
        </Field>
      </form>
    </ModalBase>
  );
};

export default NewAssetModal;
